import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMethod, postMethod, patchMethod } from '../../api/services';

export const CARDS = '/partner_cards';
export const BANNERS = '/featured_banners';

export const APPS = [
  { code: 'vsk', name: 'Swatch King' },
  { code: 'vkcl', name: 'SA Variants' },
  { code: 'via', name: 'Variant Image Automator' },
  { code: 'vtk', name: 'Variant Title King' },
  { code: 'vdk', name: 'Variant Descriptions King' },
  { code: 'ata', name: 'Alt Text King' },
  { code: 'asap', name: 'Urgency King' },
];

export function usePromotions({ resource, params = {}, axiosInstance, ...options }) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value)
  ).toString();

  return useQuery({
    ...options,
    queryKey: [resource, params],
    queryFn: () => getMethod(query ? `${resource}?${query}` : resource, axiosInstance),
    select: (response) => response.data.data,
  });
}

// Uploads go out as multipart, everything else as JSON. An empty app_codes is sent as [""] because
// FormData drops empty arrays, which would make "remove from every app" a silent no-op.
function toPayload(modelKey, attributes) {
  if (!(attributes.image instanceof File)) {
    return { [modelKey]: attributes };
  }

  const form = new FormData();

  // Nested objects need bracketed keys of their own. Appending one directly stringifies it to
  // "[object Object]", which strong params then drops - so `overrides` was silently discarded, and
  // uploading an image lost every per-app date.
  const append = (path, value) => {
    if (value === null || value === undefined) return;

    if (Array.isArray(value)) {
      if (!value.length) form.append(`${path}[]`, '');
      value.forEach((item) => form.append(`${path}[]`, item));
    } else if (value instanceof File) {
      form.append(path, value);
    } else if (typeof value === 'object') {
      Object.entries(value).forEach(([key, nested]) => append(`${path}[${key}]`, nested));
    } else {
      form.append(path, value);
    }
  };

  Object.entries(attributes).forEach(([key, value]) => append(`${modelKey}[${key}]`, value));

  return form;
}

export function usePromotionRecord({ resource, id, axiosInstance }) {
  // Every state, including archived, or archiving is a one-way trip.
  const { data, isLoading, isError } = usePromotions({ resource, params: { state: 'all' }, axiosInstance });

  return {
    record: data ? data.find((item) => String(item.id) === String(id)) : undefined,
    isLoading,
    isError,
  };
}

export function useCreatePromotion({ resource, modelKey, axiosInstance, ...options }) {
  const client = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (attributes) => postMethod(resource, toPayload(modelKey, attributes), axiosInstance),
    onSuccess: (...args) => {
      client.invalidateQueries({ queryKey: [resource] });
      if (options.onSuccess) options.onSuccess(...args);
    },
  });
}

export function useUpdatePromotion({ resource, modelKey, axiosInstance, ...options }) {
  const client = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, ...attributes }) =>
      patchMethod(`${resource}/${id}`, axiosInstance, toPayload(modelKey, attributes)),
    onSuccess: (...args) => {
      client.invalidateQueries({ queryKey: [resource] });
      if (options.onSuccess) options.onSuccess(...args);
    },
  });
}

export function useReorderPromotions({ resource, axiosInstance, ...options }) {
  const client = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ appCode, orderedIds }) =>
      patchMethod(`${resource}/reorder`, axiosInstance, {
        app_code: appCode,
        ordered_ids: orderedIds,
      }),
    onSuccess: (...args) => {
      client.invalidateQueries({ queryKey: [resource] });
      if (options.onSuccess) options.onSuccess(...args);
    },
  });
}
